import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGames, usePreviousGamesInfinite, useNextGamesInfinite } from '../hooks/gameQueries';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { Fade } from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import { groupGamesByDate, findClosestDateToToday, getCardWidth } from './utils/gameUtils';
import { GamesListProps } from '../types/Games';
import GamesContent from './GameContent';
import GamesContentList from './GamesContentList';
import GamesContentCalendar from './GamesContentCalendar';

type GamesListExtraProps = {
  viewMode?: 'cards' | 'list' | 'calendar';
  focusTeamId?: number;
  calendarMonth?: Date;
};

const GamesList = ({
  teamId,
  seasonId,
  streamTypes,
  date,
  days = 5,
  containerRef,
  viewMode = 'cards',
  focusTeamId,
  calendarMonth,
}: GamesListProps & GamesListExtraProps) => {
  // Core state
  const [isInitialPreparing, setIsInitialPreparing] = useState(true);
  const [isContentReady, setIsContentReady] = useState(false);
  const [activeStickyDate, setActiveStickyDate] = useState<string | null>(null);
  const [previousGamesEnabled, setPreviousGamesEnabled] = useState(false);

  // Refs
  const hiddenContainerRef = useRef<HTMLDivElement | null>(null);
  const dateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const initialScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memo values - use today's date as reference for UI positioning when no date is selected
  const referenceDate = useMemo(() => format(date || new Date(), 'yyyy-MM-dd'), [date]);

  // Calculate responsive card dimensions
  const cardWidth = getCardWidth();
  const cardHeight = Math.round(cardWidth / 1.66); // 16:9 ratio

  // Initial data fetch with enough buffer to avoid loading states
  const {
    data: initialData,
    isLoading: isInitialLoading,
    error: initialError,
  } = useGames({
    teamId,
    seasonId,
    streamTypes,
    date,
    limit: 20,
    upcomingLimit: 20,
  });

  // Infinite queries for previous and next games
  const {
    data: previousGamesData,
    fetchNextPage: fetchMorePreviousGames,
    hasNextPage: hasMorePreviousGames,
    isFetchingNextPage: isFetchingPreviousGames,
  } = usePreviousGamesInfinite({
    teamId,
    seasonId,
    days,
    streamTypes,
    initialData,
    enabled: isContentReady && previousGamesEnabled,
  });

  const {
    data: nextGamesData,
    fetchNextPage: fetchMoreNextGames,
    hasNextPage: hasMoreNextGames,
    isFetchingNextPage: isFetchingNextGames,
  } = useNextGamesInfinite({
    teamId,
    seasonId,
    days,
    streamTypes,
    initialData,
    enabled: isContentReady,
  });

  // Button click handlers
  const handleLoadPrevious = useCallback(() => {
    if (!previousGamesEnabled) {
      setPreviousGamesEnabled(true);
      return;
    }
    if (hasMorePreviousGames && !isFetchingPreviousGames) {
      fetchMorePreviousGames();
    }
  }, [previousGamesEnabled, hasMorePreviousGames, isFetchingPreviousGames, fetchMorePreviousGames]);

  const handleLoadNext = useCallback(() => {
    if (hasMoreNextGames && !isFetchingNextGames) {
      fetchMoreNextGames();
    }
  }, [hasMoreNextGames, isFetchingNextGames, fetchMoreNextGames]);

  // Combine all games for rendering, deduplicating by ID
  // (pages overlap by 1 day to handle UTC vs local timezone boundary)
  const allGames = useMemo(() => {
    if (!initialData) return [];

    const prevGames = previousGamesData?.pages.flatMap((page) => page.games) || [];
    const initialGames = initialData.games;
    const nextGames = nextGamesData?.pages.flatMap((page) => page.games) || [];

    const combined = [...prevGames, ...initialGames, ...nextGames];
    const seen = new Set<number>();
    return combined.filter((game) => {
      if (seen.has(game.id)) return false;
      seen.add(game.id);
      return true;
    });
  }, [initialData, previousGamesData, nextGamesData]);

  // Group games by date
  const gamesByDate = useMemo(
    () => groupGamesByDate(allGames, referenceDate, true, true),
    [allGames, referenceDate],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (initialScrollTimeoutRef.current) {
        clearTimeout(initialScrollTimeoutRef.current);
      }
    };
  }, []);

  // Set up sticky date header observer
  useEffect(() => {
    if (isInitialPreparing || !containerRef?.current) return;

    const observerOptions = {
      root: containerRef.current,
      rootMargin: '-1px 0px 0px 0px',
      threshold: [0, 1],
    };

    const visibleDates: Array<{ date: string; top: number }> = [];

    const updateActiveStickyDate = () => {
      if (visibleDates.length === 0) {
        setActiveStickyDate(null);
        return;
      }

      // Sort by top position and find top-most sticky date
      const sortedDates = [...visibleDates].sort((a, b) => a.top - b.top);
      const activeDate = sortedDates[0].top < 0 ? sortedDates[0].date : null;
      setActiveStickyDate(activeDate);
    };

    const observer = new IntersectionObserver((entries) => {
      let needsUpdate = false;

      entries.forEach((entry) => {
        const dateElement = entry.target as HTMLElement;
        const date = dateElement.dataset.date;
        if (!date) return;

        const rect = entry.boundingClientRect;
        const index = visibleDates.findIndex((item) => item.date === date);

        if (entry.isIntersecting) {
          if (index !== -1) {
            visibleDates[index].top = rect.top;
          } else {
            visibleDates.push({ date, top: rect.top });
          }
          needsUpdate = true;
        } else if (index !== -1) {
          visibleDates.splice(index, 1);
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        updateActiveStickyDate();
      }
    }, observerOptions);

    // Update positions while scrolling
    const handleScroll = () => {
      let needsUpdate = false;
      visibleDates.forEach((item) => {
        const element = dateRefs.current[item.date];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (item.top !== rect.top) {
            item.top = rect.top;
            needsUpdate = true;
          }
        }
      });

      if (needsUpdate) {
        updateActiveStickyDate();
      }
    };

    // Observe all date headers
    Object.entries(dateRefs.current).forEach(([date, element]) => {
      if (element) {
        element.dataset.date = date;
        observer.observe(element);
      }
    });

    containerRef.current.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      containerRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [gamesByDate, containerRef, isInitialPreparing]);

  // Initial setup and positioning
  useEffect(() => {
    if (isInitialLoading || !initialData || !Array.isArray(gamesByDate)) return;
    if (isContentReady) return;

    // Clear any previous timeout
    if (initialScrollTimeoutRef.current) {
      clearTimeout(initialScrollTimeoutRef.current);
    }

    initialScrollTimeoutRef.current = setTimeout(() => {
      const targetDate = findClosestDateToToday(gamesByDate, referenceDate);
      const targetElement = targetDate ? dateRefs.current[targetDate] : null;

      if (targetElement && hiddenContainerRef.current) {
        const elementOffsetTop = targetElement.offsetTop;
        const scrollTarget = elementOffsetTop;

        // Set scroll position
        if (containerRef?.current) {
          containerRef.current.scrollTop = scrollTarget;
        }
        hiddenContainerRef.current.scrollTop = scrollTarget;

        setIsInitialPreparing(false);
        setTimeout(() => setIsContentReady(true), 100);
      } else {
        console.warn('No target date found or target element is null. Defaulting to initial state.');
        setIsInitialPreparing(false);
        setIsContentReady(true);
      }
    }, 100);
  }, [isInitialLoading, initialData, gamesByDate, referenceDate, containerRef]);

  // Handle error states
  if (initialError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading games: {initialError.message}
      </Alert>
    );
  }

  if (!initialData && !isInitialLoading) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No games found
      </Alert>
    );
  }

  const sharedContentProps = {
    gamesByDate,
    dateRefs,
    allGames,
    isFetchingPreviousGames,
    hasMorePreviousGames: !previousGamesEnabled
      ? !!initialData?.pagination?.previousCursor
      : !!hasMorePreviousGames,
    isFetchingNextGames,
    hasMoreNextGames: !!hasMoreNextGames,
    onLoadPrevious: handleLoadPrevious,
    onLoadNext: handleLoadNext,
    activeStickyDate,
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Loading overlay */}
      {(isInitialPreparing || !isContentReady) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'background.default',
            zIndex: 10,
          }}
        >
          <LoadingIndicator message="Loading games..." />
        </Box>
      )}

      {/* Hidden container for scroll position calculation (cards view only) */}
      <Box
        ref={hiddenContainerRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'auto',
          visibility: 'hidden',
          zIndex: -1,
        }}
      >
        <GamesContent
          {...sharedContentProps}
          cardHeight={cardHeight}
        />
      </Box>

      {/* Visible container */}
      <Fade in={isContentReady} timeout={900}>
        <Box
          sx={{
            visibility: isContentReady ? 'visible' : 'hidden',
            opacity: isContentReady ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          {viewMode === 'cards' ? (
            <GamesContent
              {...sharedContentProps}
              cardHeight={cardHeight}
            />
          ) : viewMode === 'list' ? (
            <GamesContentList {...sharedContentProps} />
          ) : viewMode === 'calendar' && focusTeamId && calendarMonth ? (
            <GamesContentCalendar
              gamesByDate={gamesByDate}
              allGames={allGames}
              focusTeamId={focusTeamId}
              currentMonth={calendarMonth}
              isFetchingPreviousGames={isFetchingPreviousGames}
              isFetchingNextGames={isFetchingNextGames}
            />
          ) : null}
        </Box>
      </Fade>
    </Box>
  );
};

export default GamesList;